import { useEffect, useRef, useState } from 'react'

export function useAutoSave(sessionId, data, onSave, enabled = true) {
  const [saveState, setSaveState] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'
  const timerRef = useRef(null)
  const dataRef = useRef(data)
  const lastSavedRef = useRef(null)

  useEffect(() => { dataRef.current = data }, [data])

  // Serialise data for comparison so new object references with the
  // same content don't trigger unnecessary saves.
  const dataKey = JSON.stringify(data)

  useEffect(() => {
    if (!enabled || !sessionId) return

    // Skip save when data hasn't changed from last saved (or initial) value
    if (dataKey === lastSavedRef.current) return

    setSaveState('idle')
    clearTimeout(timerRef.current)

    timerRef.current = setTimeout(async () => {
      // Re-check after debounce in case another update superseded this one
      const currentKey = JSON.stringify(dataRef.current)
      if (currentKey === lastSavedRef.current) return

      setSaveState('saving')
      try {
        await onSave(sessionId, dataRef.current)
        lastSavedRef.current = currentKey
        setSaveState('saved')
        setTimeout(() => setSaveState('idle'), 2000)
      } catch (_) {
        setSaveState('error')
      }
    }, 800)

    return () => clearTimeout(timerRef.current)
  }, [sessionId, dataKey, enabled, onSave])

  // Reset the saved baseline when the session or enabled state changes,
  // so we don't skip the first real save after switching sessions.
  useEffect(() => {
    lastSavedRef.current = dataKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, enabled])

  return saveState
}
